import type { ProseSection } from "@/components/marketing/RichProse";
import { cityWhoSection } from "./whoRuns";

const R = "/research/conversion-tracking-report";

export interface LocalPage {
  path: string;
  title: string;
  description: string;
  eyebrow: string;
  h1Lead: string;
  h1Italic: string;
  lede: string;
  breadcrumb: string;
  /** Service schema areaServed; omitted for non-city pages. */
  city?: string;
  /** Service schema areaServed as a state, for statewide pages. */
  region?: string;
  /** Section rendered first, directly under the hero. */
  intro?: ProseSection;
  sections: ProseSection[];
  /** Renders the metro comparison graphic after this section index. */
  metroAfter?: number;
  /** FAQ heading label; defaults to the city. */
  faqLabel?: string;
}

export const LSA_ROOFERS: LocalPage = {
  path: "/services/local-services-ads-for-roofers",
  title: "Local Services Ads for Roofers: Setup, Storms & Leads",
  description:
    "Local Services Ads management for roofers: Google Verified screening, storm-week lead flow, lead review and credits, and retail vs. insurance work.",
  eyebrow: "Services · Roofing",
  h1Lead: "Local Services Ads",
  h1Italic: "for roofers.",
  lede:
    "Where our Local Services Ads work and our roofing work meet. We run LSA for roofing companies today, and this page is about how it behaves in that trade specifically.",
  breadcrumb: "Local Services Ads for Roofers",
  sections: [
    {
      heading: "What holds up Google Verified approval for a roofer?",
      paragraphs: [
          "Approval usually stalls on document mismatches, not the background checks. Google screens the owner and field staff, and needs the state contractor license and liability insurance in the names and categories it expects.",
          "Typical mismatches: a license under a different entity than the Google Business Profile, a lapsed insurance certificate, or roofing filed under a license class that does not cover it.",
          "We line documents up before applying, and upload renewals so the badge does not lapse mid-season. General mechanics are on our [Local Services Ads management](/services/local-services-ads) page.",
        ],
    },
    {
      id: "optimize",
      heading: "How do you optimize Local Services Ads for roofers?",
      paragraphs: [
        "Google ranks Local Services Ads with an auction that weighs your bid alongside the quality of your profile, so most of the work is the profile, not the bid.",
      ],
      points: [
        '01 Match your job types and service area to the work you actually do. Google no longer credits leads marked "job type not serviced" or "geo not serviced", so a profile that lists repairs you will not take, or towns you will not drive to, now pays for those leads.',
        "02 Answer fast. Google counts missed calls against your responsiveness, and your average response time is part of your profile quality. Set business hours your team can really cover, and route calls so they get picked up.",
        "03 Keep the reviews coming. Google says star ratings and the number of reviews affect how you rank in Local Services Ads, and those reviews are managed through your Google Business Profile, so every review there counts.",
        "04 Finish the profile. Google lists high-quality images and the verification checks you have completed as part of profile quality, and weighs how relevant your services and business bio are to the search. Google says higher-quality profiles may rank higher and may pay lower costs per lead.",
        "05 Then set the bid. Your bid is the most you are willing to pay for a lead, and it still matters in the auction, but fix 01 to 04 before raising it. For storm weeks, see the next section.",
      ],
      note: 'Source: Google Local Services Help, ["About ad rankings"](https://support.google.com/localservices/answer/7527305) and ["Reviews and ratings"](https://support.google.com/localservices/answer/7496631).',
    },
    {
      heading: "What happens to LSA lead flow when a storm hits?",
      paragraphs: [
          "LSA is pay-per-lead, so a storm raises both volume and competition at once: homeowners search for damage help, and every roofer in the metro raises budgets.",
          "A weekly budget that lasts a quiet week can be gone by midweek, on a mix of real damage inquiries, shoppers calling five companies, and calls for work the business does not do.",
          "We plan storm budgets before the season and watch lead quality by day, since every poor lead is a charge to review. Our [roofing industry page](/industries/roofing) covers storm response across channels.",
        ],
    },
    {
      heading: "What happens to bad roofing leads?",
      paragraphs: [
          "Bad leads still get charged: wrong numbers, solicitors, out-of-area calls and non-roofing work.",
          "Google credits low-quality leads automatically, usually within 30 days, but no longer credits job type or geo not serviced leads, so the profile must stay accurate.",
          "We review recordings and lead details on a schedule, use Rate this lead, and keep job types and service area current. The old process is in [how LSA lead credits used to work](/blog/mastering-lsa-disputes-recover-20-of-monthly-ad-spend) and [How LSA lead disputes used to work](/blog/mastering-lsa-disputes-recover-20-of-monthly-ad-spend).",
        ],
    },
    {
      heading: "Why shouldn't a roofer run Local Services Ads on their own?",
      paragraphs: [
          "LSA only covers a narrow set of queries: a homeowner searching for a roofer or roof repair nearby.",
          "It misses research, brand and material searches, and homeowners who have not started searching, so an LSA-only roofer is invisible there.",
          "Search campaigns in [Google Ads](/services/google-ads) cover what LSA cannot, and [Meta Ads](/services/meta-ads) reach affected neighborhoods before people search. See [structuring roofing Google Ads and LSA for retail leads](/blog/how-to-structure-roofing-google-ads-and-lsa-for-retail-leads).",
        ],
    },
    {
      heading: "Why are retail and insurance roofing leads different products?",
      paragraphs: [
          "They arrive through the same LSA listing at similar cost per lead, but are worth different amounts on very different timelines.",
          "A retail re-roof replaces a roof at the end of its life. An insurance job starts with storm damage and an adjuster, runs on the claim's timeline, and is partly decided by the insurer.",
          "Sharing one target hides which campaigns produce which. We tag them separately at intake and report them apart, so budget follows the kind of job, not the blended average.",
        ],
    },
    {
      heading: "How many roofers can measure what their ads produce?",
      paragraphs: [
        `We loaded 297 roofing websites in a real browser. 219 of them, 73.7%, had no conversion tracking of any kind: figures from our [conversion tracking report](${R}). LSA reports its own charged leads, but only those. Without tracking on the website and the phone line, everything outside LSA is unmeasured, and the account cannot compare LSA against Search or Meta on equal terms.`,
      ],
    },
  ],
};

export const CITY_PAGES: Record<string, LocalPage> = {
  phoenix: {
    path: "/phoenix-ppc-agency",
    city: "Phoenix",
    title: "Phoenix PPC Agency: Google, Meta & LSA for Home Services",
    description:
      "We loaded 198 Phoenix business websites: 13 of the 52 running ads had no tracking. Paid media for Phoenix operators, from a Scottsdale team.",
    eyebrow: "Phoenix, AZ · Served from Scottsdale",
    h1Lead: "Phoenix PPC agency",
    h1Italic: "for Phoenix operators.",
    lede:
      "Phoenix is the biggest market we scanned in the metro, and the one with the most money going into ads that nothing is measuring.",
    breadcrumb: "Phoenix PPC agency",
    sections: [
      {
        heading: "How much Phoenix ad spend has nothing measuring it?",
        paragraphs: [
          "Of the 52 Phoenix businesses we confirmed were running ads, 13 of those 52, 25.0%, had nothing measuring the result: the highest blind-advertiser rate of the four metro cities.",
          "Across 198 Phoenix websites, our largest city sample, 128 of them, 64.6%, had no conversion tracking. Figures were read on 6 October 2026 from the scans behind our [conversion tracking report](/research/conversion-tracking-report), as a city cut outside its published table.",
        ],
      },
      {
        heading: "Why does that matter more in Phoenix than elsewhere?",
        paragraphs: [
          "Phoenix is the metro's most competitive market, so blind advertising costs more here: crowded auctions push click prices up, and one in four confirmed advertisers pays them on guesswork.",
          "Phoenix still measures better than our overall sample: 64.6% against 70.1% across all 9,560 sites. The market is crowded, not failing, and those with working measurement bid with confidence while the rest bid on hope.",
        ],
      },
      {
        heading: "Google Ads, Facebook ads and LSA management in Phoenix",
        paragraphs: [
          "Measurement first, then structure: [Google Ads](/services/google-ads) split by service and intent so expensive searches are bid on their own value, and [Local Services Ads](/services/local-services-ads) for the call-now demand that dominates home services here.",
          "We work from [Scottsdale](/scottsdale-ppc-agency) and serve Phoenix operators (no Phoenix office). Our [free conversion tracking audit](/audit) loads your site like our scan does and shows which side of 13-of-52 you sit on.",
        ],
        points: [
          "01 [Google Ads management](/services/google-ads): Search campaigns separate services and intent, with conversion tracking showing which calls become booked jobs.",
          "02 [Facebook and Instagram ads](/services/meta-ads): Meta campaigns reach Phoenix homeowners before they search and bring interested visitors back.",
          "03 [Local Services Ads](/services/local-services-ads): LSA reaches people ready to call, with job types and service areas matched to the work you take.",
        ],
      },
    ],
  },
  scottsdale: {
    path: "/scottsdale-ppc-agency",
    city: "Scottsdale",
    title: "Scottsdale PPC Agency: Google Ads, LSA & Meta Management",
    description:
      "PPC management from a Scottsdale team: Google Ads, Local Services Ads and Meta for local businesses, judged on leads and booked jobs.",
    eyebrow: "Scottsdale, AZ · Our home market",
    h1Lead: "Scottsdale PPC agency,",
    h1Italic: "in our home market.",
    lede:
      "We are based in Scottsdale. It is also the best-measuring city of the four we cut our data for, which makes it a harder market to win on measurement alone.",
    breadcrumb: "Scottsdale PPC agency",
    sections: [
      {
        heading: "How well do Scottsdale businesses measure their advertising?",
        paragraphs: [
          "Better than anywhere else we measured: 93 of 161 Scottsdale business websites, 57.8%, had no conversion tracking, the lowest of the four metro cities and well under 70.1% overall.",
          "Of the 37 we confirmed were running ads, 7 of the 37, 18.9%, were advertising blind. Figures were read on 6 October 2026 from the scans behind our [conversion tracking report](/research/conversion-tracking-report), as a subset of that book.",
        ],
      },
      {
        heading: "PPC management in Scottsdale",
        paragraphs: ["Our Scottsdale-based team manages Google Ads, Local Services Ads and Meta for Scottsdale businesses. Every account is judged on booked jobs, not clicks."],
        points: [
          "01 [Google Ads management](/services/google-ads): We organize Search campaigns around the services you sell and measure which searches become booked jobs.",
          "02 [Local Services Ads](/services/local-services-ads): We manage the profile, service area and lead flow for customers ready to call.",
          "03 [Meta and Facebook ads](/services/meta-ads): We reach nearby homeowners on Facebook and Instagram and measure qualified calls and booked jobs.",
        ],
      },
      {
        heading: "Why is a well-measured market harder to win?",
        paragraphs: [
          "Because tracking stops being the edge: in Scottsdale more of the competition already measures, so it is the minimum to compete.",
          "The edge moves down the funnel: which leads become booked jobs, what each is worth, and whether that value reaches the ad platforms so bidding learns from revenue, not form fills.",
        ],
      },
      {
        heading: "Working with a team based in Scottsdale",
        paragraphs: [
          "Scottsdale is where we are based, so meeting in person is easy, and the team that measures this market manages your [Google Ads](/services/google-ads), [Meta Ads](/services/meta-ads) and [Local Services Ads](/services/local-services-ads).",
          "For home services operators and local brands, our [free conversion tracking audit](/audit) loads your site in a real browser and shows which tags fire, no email required.",
        ],
      },
    ],
  },
  mesa: {
    path: "/mesa-ppc-agency",
    city: "Mesa",
    title: "Mesa PPC Agency: Google Ads & LSA for Mesa Businesses",
    description:
      "Mesa measured worst of the four metro cities we scanned: 115 of 165 sites, 69.7%, had no conversion tracking. Paid media for Mesa operators.",
    eyebrow: "Mesa, AZ · Served from Scottsdale",
    h1Lead: "Mesa PPC agency",
    h1Italic: "for Mesa businesses.",
    lede:
      "Mesa is the worst-measuring city of the four we cut our data for: 11.9 percentage points behind Scottsdale, twenty minutes up the road.",
    breadcrumb: "Mesa PPC agency",
    sections: [
      {
        heading: "Why does Mesa measure worse than its neighbors?",
        paragraphs: [
          "We do not have a measured explanation, and we will not invent one. The scan shows the outcome: 115 of 165 Mesa business websites, 69.7%, had no conversion tracking, the highest rate of the four metro cities.",
          "[Scottsdale](/scottsdale-ppc-agency), about twenty minutes away, was at 57.8%: an 11.9-point gap. Figures were read on 6 October 2026 from the scans behind our [conversion tracking report](/research/conversion-tracking-report).",
        ],
      },
      {
        heading: "What does that mean for a Mesa business buying ads?",
        paragraphs: [
          "An opportunity: where fewer competitors can see their own results, the ones who can bid with information the rest do not have.",
          "Of the 36 Mesa businesses we confirmed were advertising, 8 of the 36, 22.2%, had no conversion tracking. Mesa sits just under the 70.1% rate across our whole sample: the metro's weakest city, not an outlier.",
        ],
      },
      {
        heading: "Where to start in Mesa",
        paragraphs: [
          "Start with the measurement gap, then build [Google Ads](/services/google-ads) and [Local Services Ads](/services/local-services-ads) on it, so every later change is judged on leads and jobs, not clicks.",
          "We work from [Scottsdale](/scottsdale-ppc-agency) and serve Mesa operators (no Mesa office). Our [free conversion tracking audit](/audit) runs the same check on your site in about 15 seconds.",
        ],
      },
    ],
  },
  chandler: {
    path: "/chandler-ppc-agency",
    city: "Chandler",
    title: "Chandler PPC Agency: Paid Media for Chandler Businesses",
    description:
      "In Chandler, 5 of the 31 businesses we confirmed advertising had no tracking: the lowest of four metro cities. Paid media for Chandler operators.",
    eyebrow: "Chandler, AZ · Served from Scottsdale",
    h1Lead: "Chandler PPC agency",
    h1Italic: "for Chandler businesses.",
    lede:
      "Chandler had the smallest sample of the four metro cities we cut our data for, and the lowest share of advertisers running ads blind.",
    breadcrumb: "Chandler PPC agency",
    sections: [
      {
        heading: "How do Chandler advertisers compare with the rest of the metro?",
        paragraphs: [
          "Well: 64 of 109 Chandler business websites, 58.7%, had no conversion tracking, second only to [Scottsdale](/scottsdale-ppc-agency) among the four cities.",
          "Of the 31 we confirmed were running ads, 5 of the 31, 16.1%, were advertising blind, the lowest of the four. With 31 advertisers, one business changing its setup would move that rate several points.",
          "Figures were read on 6 October 2026 from the scans behind our [conversion tracking report](/research/conversion-tracking-report).",
        ],
      },
      {
        heading: "If most Chandler advertisers measure, where is the gap?",
        paragraphs: [
          "In what the account learns from each lead, not whether it is counted.",
          "That means counting calls alongside forms, separating booked jobs from inquiries, and sending that back so automated bidding optimizes toward work, not volume. Our scan cannot see this from outside, and it decides a well-measured market.",
        ],
      },
      {
        heading: "What we run for Chandler operators",
        paragraphs: [
          "We run [Google Ads](/services/google-ads) and [Meta Ads](/services/meta-ads) for home services operators and local brands, checking conversion setup before any budget decision.",
          "We are based in [Scottsdale](/scottsdale-ppc-agency) and serve Chandler businesses (no Chandler office). To see whether your site is among the 5 or the 26, run our [free conversion tracking audit](/audit).",
        ],
      },
    ],
  },
};

for (const page of Object.values(CITY_PAGES)) if (page.city) page.intro = cityWhoSection(page.city);

const ASK_BASE = [
  "Do you run Local Services Ads as well as Search, and who rates bad leads?",
];

export const ARIZONA_PAGES: Record<string, LocalPage> = {
  arizona: {
    path: "/arizona-home-services-ppc-agency",
    region: "Arizona",
    faqLabel: "Arizona",
    title: "Home Services PPC Agency in Arizona | Berk Hospitality Group",
    description: "Google Ads, Local Services Ads and Meta Ads for home services companies across Arizona, from a Scottsdale team, judged on booked jobs.",
    eyebrow: "Arizona · Home services",
    h1Lead: "PPC for home services companies",
    h1Italic: "in Arizona.",
    lede: "Google Ads, Local Services Ads and Meta Ads for Arizona contractors and service businesses, from a team based in Scottsdale.",
    breadcrumb: "Home services PPC in Arizona",
    metroAfter: 3,
    sections: [
      {
        heading: "Who runs PPC for home services companies in Arizona?",
        paragraphs: ["Berk Hospitality Group is a Scottsdale agency, a certified Google Partner and a Meta Business Partner. We manage Google Ads, Local Services Ads and Meta Ads for home services companies in Arizona and other states, and accounts are judged on booked jobs, not clicks."],
      },
      {
        heading: "How we judge the work",
        paragraphs: [
          "Lead counts say whether ads are running. Invoices say whether they are worth it. On an Arizona restoration account, we matched the jobs from the ads to the invoices, one job at a time, so the owner could see what the ads actually brought in.",
          "Elsewhere, one landscaping lead closed at $90,000 and two roofing deals in one month came to $50,000. Those are in our [case studies](/case-studies), reported the way we report to clients.",
        ],
      },
      {
        heading: "What we manage",
        paragraphs: [],
        points: [
          "01 [Google Ads](/services/google-ads): Search campaigns organized around the services you sell, measured on which searches become booked jobs.",
          "02 [Local Services Ads](/services/local-services-ads): verification, job types, service area, the weekly budget and lead review.",
          "03 [Meta Ads](/services/meta-ads): Facebook and Instagram for nearby homeowners, measured on qualified calls and booked jobs.",
        ],
      },
      {
        heading: "How well do Arizona home services companies measure their advertising?",
        paragraphs: [`We loaded 2,242 Arizona home services websites in a real browser. 1,526 of them, 68.1%, had no conversion tracking. Read on 2 November 2026 from the scans behind our [conversion tracking report](${R}), as an Arizona cut outside its published table.`],
      },
      {
        heading: "Arizona cities and trades",
        paragraphs: [],
        bullets: [
          "[Scottsdale](/scottsdale-ppc-agency)",
          "[Phoenix](/phoenix-ppc-agency)",
          "[Mesa](/mesa-ppc-agency)",
          "[Chandler](/chandler-ppc-agency)",
          "[HVAC companies in Arizona](/google-ads-for-hvac-companies-arizona)",
          "[Roofing companies in Arizona](/google-ads-for-roofing-companies-arizona)",
          "[Arizona agencies compared](/blog/ppc-agencies-for-home-services-companies-in-arizona)",
        ],
      },
    ],
  },
  hvac: {
    path: "/google-ads-for-hvac-companies-arizona",
    region: "Arizona",
    faqLabel: "Arizona HVAC",
    title: "Google Ads for HVAC Companies in Arizona | Berk Hospitality Group",
    description: "Google Ads and Local Services Ads for HVAC companies in Scottsdale, Phoenix and across Arizona, from a Scottsdale team, judged on booked jobs.",
    eyebrow: "Arizona · HVAC",
    h1Lead: "Google Ads for HVAC companies",
    h1Italic: "in Scottsdale, Phoenix and across Arizona.",
    lede: "Google Search and Local Services Ads built around repair, replacement and maintenance, from a team based in Scottsdale.",
    breadcrumb: "Google Ads for HVAC companies in Arizona",
    sections: [
      {
        heading: "Who runs Google Ads for an HVAC company in Scottsdale?",
        paragraphs: ["Berk Hospitality Group does. We are a Scottsdale agency and certified Google Partner that runs Google Ads and Local Services Ads for home services companies, and we work with HVAC companies across Arizona. Accounts are judged on booked jobs, not clicks. Scottsdale is our home market, so meeting in person is easy."],
      },
      {
        heading: "What should be different about an Arizona HVAC account?",
        paragraphs: [
          "The season drives everything. Summer is when no-cool calls peak and when every operator raises budgets, so budgets are planned before the heat arrives, not during it.",
          "Repair, replacement and maintenance need separate campaigns. A no-cool call, a system replacement and a tune-up are three different searches with three different values, and tune-up leads should not take budget from replacement keywords.",
          "Local Services Ads sit alongside Search for urgent calls. Position there follows reviews, proximity and whether the phone gets answered, so review requests and answer rate are part of the work.",
        ],
      },
      {
        heading: "How well do Arizona HVAC companies measure their advertising?",
        paragraphs: [`We loaded 62 Arizona HVAC websites in a real browser. 36 of them, 58.1%, had no conversion tracking, so those sites could not report which ads produced a lead. Read on 2 November 2026 from the scans behind our [conversion tracking report](${R}), as an Arizona cut outside its published table.`],
      },
      {
        heading: "What should you ask an agency before hiring one?",
        paragraphs: [],
        bullets: [
          "Do you report booked jobs, or only leads and clicks?",
          ...ASK_BASE,
          "Can a repair, a replacement and a maintenance plan be reported separately?",
          "Who works on the account each week?",
          "What happens to the account and its data if we leave?",
        ],
      },
      {
        heading: "Where to go next",
        paragraphs: [],
        bullets: [
          "[HVAC paid media](/industries/hvac)",
          "[Local Services Ads management](/services/local-services-ads)",
          "[Home services PPC in Arizona](/arizona-home-services-ppc-agency)",
          "[Scottsdale PPC agency](/scottsdale-ppc-agency)",
          "[Phoenix PPC agency](/phoenix-ppc-agency)",
          "[Free conversion tracking audit](/audit)",
        ],
      },
    ],
  },
  roofing: {
    path: "/google-ads-for-roofing-companies-arizona",
    region: "Arizona",
    faqLabel: "Arizona roofing",
    title: "Google Ads for Roofing Companies in Arizona | Berk Hospitality Group",
    description: "Google Ads and Local Services Ads for roofing companies in Phoenix, Scottsdale and across Arizona, from a Scottsdale team, judged on closed work.",
    eyebrow: "Arizona · Roofing",
    h1Lead: "Google Ads for roofing companies",
    h1Italic: "in Phoenix, Scottsdale and across Arizona.",
    lede: "Google Search and Local Services Ads for roofers, judged on closed work, from a team based in Scottsdale.",
    breadcrumb: "Google Ads for roofing companies in Arizona",
    sections: [
      {
        heading: "Who runs Google Ads for roofing companies in Phoenix?",
        paragraphs: ["Berk Hospitality Group does. We are a Scottsdale agency and certified Google Partner, and we run Google Ads and Local Services Ads for roofing companies. For one roofing contractor, two deals in a single month came to [$50,000 in closed work](/case-studies)."],
      },
      {
        heading: "What should be different about an Arizona roofing account?",
        paragraphs: [
          "Repairs, replacements and inspections are different searches with different values, so they need separate campaigns and separate reporting.",
          "Roofing leads arrive as calls, so if call tracking is misconfigured the ad platform records nothing. Measurement comes before any bid or budget work.",
          "[Local Services Ads](/services/local-services-ads-for-roofers) approval usually stalls on document mismatches, not background checks.",
        ],
      },
      {
        heading: "How well do Arizona roofers measure their advertising?",
        paragraphs: [`We loaded 103 Arizona roofing websites in a real browser. 67 of them, 65.0%, had no conversion tracking. Read on 2 November 2026 from the scans behind our [conversion tracking report](${R}), as an Arizona cut outside its published table.`],
      },
      {
        heading: "What should you ask an agency before hiring one?",
        paragraphs: [],
        bullets: [
          "Do you report closed work, or only leads and clicks?",
          ...ASK_BASE,
          "Are calls tracked and tied back to the campaign that produced them?",
          "Who works on the account each week?",
          "What happens to the account and its data if we leave?",
        ],
      },
      {
        heading: "Where to go next",
        paragraphs: [],
        bullets: [
          "[Roofing paid media](/industries/roofing)",
          "[Local Services Ads for roofers](/services/local-services-ads-for-roofers)",
          "[Home services PPC in Arizona](/arizona-home-services-ppc-agency)",
          "[Phoenix PPC agency](/phoenix-ppc-agency)",
          "[Free conversion tracking audit](/audit)",
        ],
      },
    ],
  },
};
